import { useMemo, type FC } from 'react';
import { useStorybook } from 'src/hooks/use-storybook';
import { cn } from 'src/utils/cn';
import { reportToSentry } from 'src/utils/report-to-sentry';

const LOGO_SIZES = {
  xs: {
    width: 104,
    height: 48,
    // we want to use an arbitrary value here, not a dimension design token
    // and Tailwind Intellisense will still complain, because it doesn't have supress comments support
    // oxlint-disable-next-line better-tailwindcss/enforce-canonical-classes
    className: cn('h-[48px] w-[104px]'),
  },
  sm: {
    width: 130,
    height: 60,
    className: cn('h-[60px] w-[130px]'),
  },
} as const;

const PIXEL_RATIO_BUCKETS = [1, 2, 3, 4] as const;

type Size = keyof typeof LOGO_SIZES;

type Props = {
  url: string;
  name: string;
  size?: Size;
  'data-testid'?: string;
};

export const OrganisationLogo: FC<Props> = ({
  url,
  name,
  size = 'sm',
  'data-testid': testId = 'organisation-logo',
}) => {
  const { isStorybook } = useStorybook();
  const sizeConfig = LOGO_SIZES[size];

  const logoSources = useMemo(() => {
    try {
      const validatedUrl = getLogoUrl(url, isStorybook);
      const sources = PIXEL_RATIO_BUCKETS.map((bucket) => ({
        bucket,
        url: buildLogoUrl(validatedUrl, sizeConfig, bucket),
      }));
      return {
        src: sources.find((source) => source.bucket === 1)?.url,
        srcSet: sources
          .map((source) => `${source.url} ${source.bucket}x`)
          .join(', '),
      };
    } catch (error) {
      reportToSentry(error);
      console.error(error);
      return {
        src: undefined,
        srcSet: undefined,
      };
    }
  }, [url, isStorybook, sizeConfig]);

  return (
    <img
      src={logoSources.src}
      srcSet={logoSources.srcSet}
      alt={`Logo of ${name}`}
      className={cn('block object-contain', sizeConfig.className)}
      data-testid={testId}
    />
  );
};

function getLogoUrl(url: string, isStorybook: boolean) {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch {
    throw new Error(`Given url parameter is not a valid URL. Received: ${url}`);
  }

  if (!isStorybook) {
    const organisationServiceUrl = new URL(PRISMA_CONFIG.organisationService);

    const isOrganisationServiceLogo =
      parsedUrl.origin === organisationServiceUrl.origin;

    if (!isOrganisationServiceLogo) {
      throw new Error(
        `Given url parameter doesn't seem to be a valid organisation-service logo URL. Received: ${url}`
      );
    }
  }

  return parsedUrl;
}

function buildLogoUrl(
  baseUrl: URL,
  sizeConfig: (typeof LOGO_SIZES)[Size],
  bucket: (typeof PIXEL_RATIO_BUCKETS)[number]
) {
  const logoUrl = new URL(baseUrl.toString());

  logoUrl.searchParams.set('width', String(sizeConfig.width * bucket));
  logoUrl.searchParams.set('height', String(sizeConfig.height * bucket));

  return logoUrl.toString();
}
